import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PageEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPage = useCallback(async () => {
    try {
      const decodedSlug = decodeURIComponent(slug);
      const response = await axios.get(`${API_URL}/api/pages${decodedSlug}`, {
        withCredentials: true
      });
      setPage(response.data);
    } catch (error) {
      
      setMessage({ type: 'error', text: 'Pagina laden mislukt' });
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await axios.put(
        `${API_URL}/api/pages${page.slug}`,
        {
          page_title: page.page_title,
          seo: page.seo,
          hero: page.hero,
          status: page.status
        },
        { withCredentials: true }
      );
      setMessage({ type: 'success', text: 'Pagina succesvol opgeslagen!' });
    } catch (error) {
      console.error('Failed to save page:', error);
      setMessage({ type: 'error', text: 'Opslaan mislukt. Probeer opnieuw.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-red-600 mb-4">Pagina niet gevonden</p>
            <Link to="/admin/pages">
              <Button>Terug naar Overzicht</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/admin/pages">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Terug
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Bewerk Pagina</h1>
                <p className="text-sm text-gray-600">{page.slug}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <a href={page.slug} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
              </a>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-700 hover:bg-blue-800"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Opslaan...' : 'Opslaan'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="space-y-6">
          {/* SEO Section */}
          <Card>
            <CardHeader>
              <CardTitle>SEO Instellingen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Title *
                </label>
                <Input
                  value={page.seo.title}
                  onChange={(e) =>
                    setPage({
                      ...page,
                      seo: { ...page.seo, title: e.target.value }
                    })
                  }
                  placeholder="Pagina titel voor zoekmachines"
                  maxLength={60}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {page.seo.title.length}/60 karakters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description *
                </label>
                <Textarea
                  value={page.seo.description}
                  onChange={(e) =>
                    setPage({
                      ...page,
                      seo: { ...page.seo, description: e.target.value }
                    })
                  }
                  placeholder="Beschrijving voor zoekmachines"
                  rows={3}
                  maxLength={160}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {page.seo.description.length}/160 karakters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keywords (optioneel)
                </label>
                <Input
                  value={page.seo.keywords || ''}
                  onChange={(e) =>
                    setPage({
                      ...page,
                      seo: { ...page.seo, keywords: e.target.value }
                    })
                  }
                  placeholder="zoho crm, crm systeem, implementatie"
                />
              </div>
            </CardContent>
          </Card>

          {/* Hero Section */}
          <Card>
            <CardHeader>
              <CardTitle>Hero Sectie</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Badge Tekst (optioneel)
                </label>
                <Input
                  value={page.hero.badge_text || ''}
                  onChange={(e) =>
                    setPage({
                      ...page,
                      hero: { ...page.hero, badge_text: e.target.value }
                    })
                  }
                  placeholder="Officieel Zoho Partner"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hoofdtitel (H1) *
                </label>
                <Textarea
                  value={page.hero.title}
                  onChange={(e) =>
                    setPage({
                      ...page,
                      hero: { ...page.hero, title: e.target.value }
                    })
                  }
                  placeholder="Hoofdtitel van de pagina"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ondertitel *
                </label>
                <Textarea
                  value={page.hero.subtitle}
                  onChange={(e) =>
                    setPage({
                      ...page,
                      hero: { ...page.hero, subtitle: e.target.value }
                    })
                  }
                  placeholder="Ondertitel / beschrijving"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primaire CTA Tekst
                  </label>
                  <Input
                    value={page.hero.cta_primary_text}
                    onChange={(e) =>
                      setPage({
                        ...page,
                        hero: { ...page.hero, cta_primary_text: e.target.value }
                      })
                    }
                    placeholder="Gratis Intakegesprek"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primaire CTA Link
                  </label>
                  <Input
                    value={page.hero.cta_primary_link}
                    onChange={(e) =>
                      setPage({
                        ...page,
                        hero: { ...page.hero, cta_primary_link: e.target.value }
                      })
                    }
                    placeholder="/contact"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secundaire CTA Tekst
                  </label>
                  <Input
                    value={page.hero.cta_secondary_text}
                    onChange={(e) =>
                      setPage({
                        ...page,
                        hero: { ...page.hero, cta_secondary_text: e.target.value }
                      })
                    }
                    placeholder="Bel: 06 3882 3471"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secundaire CTA Link
                  </label>
                  <Input
                    value={page.hero.cta_secondary_link}
                    onChange={(e) =>
                      setPage({
                        ...page,
                        hero: { ...page.hero, cta_secondary_link: e.target.value }
                      })
                    }
                    placeholder="tel:0638823471"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle>Publicatie Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="published"
                    checked={page.status === 'published'}
                    onChange={(e) => setPage({ ...page, status: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Gepubliceerd
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="draft"
                    checked={page.status === 'draft'}
                    onChange={(e) => setPage({ ...page, status: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Concept
                  </span>
                </label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Save Button (Bottom) */}
        <div className="mt-8 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="bg-blue-700 hover:bg-blue-800"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Opslaan...' : 'Wijzigingen Opslaan'}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default PageEditor;
